import { axiosInstance } from "@/config/apiClient";
import { supabaseClient } from "@/config/supabaseClient";
import type {
	Plan,
	SubscriptionResponse,
	SuscripcionDb,
	PlanFeatures,
} from "../schemas";
import {
	planFeaturesSchema,
	planSchema,
	subscriptionResponseSchema,
	type PlanFlags,
	type PlanLimits,
} from "../schemas";

interface SubscriptionCacheEntry {
	userId: string | null;
	data: SubscriptionResponse;
}

const DEFAULT_PLAN_FLAGS: PlanFlags = {
	finance_access: true,
	valuation_access: true,
	refinance_access: true,
	debt_purchase_access: true,
	dashboard_access: true,
	export_data: false,
	bulk_import_finance: false,
	bulk_import_amortizacion: false,
	analytics_access: false,
};

const DEFAULT_PLAN_LIMITS: PlanLimits = {
	properties: 3,
	mortgages_per_property: 1,
	amortizations_per_mortgage: 3,
	valuations_per_property: 3,
	incomes_per_property: 3,
	expenses_per_property: 3,
	dashboard_future_horizon_years: 0,
};

const DEFAULT_PLAN_FEATURES: PlanFeatures = {
	version: 1,
	flags: DEFAULT_PLAN_FLAGS,
	limits: DEFAULT_PLAN_LIMITS,
};

// ─── Module-level subscription cache ────────────────────────────────
// Caching is done here in the API layer (not in React Query's queryFn)
// because React Query may cancel/discard queryFn results during
// StrictMode mount cycles, preventing post-fetch code from running.
// localStorage is only consulted as a fallback after a live request
// fails, and only when userId matches — never on module load.
// ─────────────────────────────────────────────────────────────────────
let _subCache: SubscriptionCacheEntry | null = null;

/**
 * Clear the in-memory + localStorage subscription cache.
 * Call this before invalidateQueries to force a real API fetch.
 */
export const clearSubscriptionApiCache = () => {
	_subCache = null;
	try { localStorage.removeItem("hip_sub_cache"); } catch { /* ignore */ }
};

const getCurrentUserId = async (): Promise<string | null> => {
	const {
		data: { session },
	} = await supabaseClient.auth.getSession();

	return session?.user?.id ?? null;
};

const parsePlanFeatures = (
	rawFeatures: unknown,
	context: string,
): PlanFeatures => {
	let parsedFeatures = rawFeatures;

	if (typeof rawFeatures === "string") {
		try {
			parsedFeatures = JSON.parse(rawFeatures);
		} catch (error) {
			console.error(`Error parsing plan features for ${context}`, error);
			return DEFAULT_PLAN_FEATURES;
		}
	}

	const featureResult = planFeaturesSchema.safeParse(parsedFeatures);
	if (!featureResult.success) {
		console.error(
			`Invalid plan features for ${context}`,
			featureResult.error.flatten(),
		);
		return DEFAULT_PLAN_FEATURES;
	}

	return featureResult.data;
};

const normalizePlan = (rawPlan: unknown, context: string): Plan | null => {
	if (!rawPlan || typeof rawPlan !== "object") {
		console.error(`Invalid plan payload for ${context}`, rawPlan);
		return null;
	}

	const planCandidate = {
		...(rawPlan as Record<string, unknown>),
		features: parsePlanFeatures(
			(rawPlan as Record<string, unknown>).features,
			context,
		),
	};

	const planResult = planSchema.safeParse(planCandidate);
	if (!planResult.success) {
		console.error(
			`Invalid plan payload for ${context}`,
			planResult.error.flatten(),
		);
		return null;
	}

	return planResult.data;
};

/**
 * Subscription API client
 */
export const subscriptionApi = {
	/**
	 * Get all available plans
	 */
	getPlans: async (): Promise<Plan[]> => {
		const response = await axiosInstance.get("/planes");
		if (!Array.isArray(response.data)) {
			console.error("Invalid plans response payload", response.data);
			return [];
		}

		return response.data
			.map((plan, index) => normalizePlan(plan, `plans[${index}]`))
			.filter((plan): plan is Plan => plan !== null);
	},

	/**
	 * Get current subscription.
	 * Supports nested and flattened backend payloads.
	 * Uses cache only as fallback if request fails.
	 */
	getSubscription: async (): Promise<SubscriptionResponse> => {
		const currentUserId = await getCurrentUserId();
		try {
			const response = await axiosInstance.get("/suscripcion");
			const rawData = response.data;

			if (!rawData || Object.keys(rawData).length === 0) {
				return { suscripcion: null };
			}

			// Support both payload shapes:
			// 1) New nested plan object: { ..., plan: {...} }
			// 2) Legacy flattened plan fields: plan_id, plan_nombre, ...
			const rawPlan = (rawData as Record<string, unknown>).plan;
			const nestedPlan =
				rawPlan && typeof rawPlan === "object"
					? normalizePlan(rawPlan, "subscription.plan") || undefined
					: undefined;

			const flattenedPlan =
				rawData.plan_id
					? normalizePlan(
							{
								id: rawData.plan_id,
								nombre: rawData.plan_nombre || "",
								descripcion: rawData.plan_descripcion || null,
								precio_mensual: rawData.plan_precio_mensual || 0,
								precio_anual: rawData.plan_precio_anual || 0,
								currency: "PEN",
								features: rawData.plan_features,
								esta_activo: rawData.plan_esta_activo ?? true,
								dias_prueba: rawData.plan_dias_prueba ?? 0,
								proveedor_plan_id_mensual:
									rawData.proveedor_plan_id_mensual ?? null,
								proveedor_plan_id_anual:
									rawData.proveedor_plan_id_anual ?? null,
							},
							"subscription.plan",
						) || undefined
					: undefined;

			const plan: Plan | undefined = nestedPlan ?? flattenedPlan;

			const suscripcion: SuscripcionDb = {
				id: rawData.id,
				usuario_id: rawData.usuario_id,
				plan_id: rawData.plan_id ?? plan?.id ?? 0,
				estado: rawData.estado,
				periodo_inicio: rawData.periodo_inicio,
				periodo_fin: rawData.periodo_fin,
				proveedor: rawData.proveedor,
				proveedor_pago_id: rawData.proveedor_pago_id,
				proveedor_cliente_id: rawData.proveedor_cliente_id,
				en_periodo_prueba: rawData.en_periodo_prueba,
				fecha_fin_prueba: rawData.fecha_fin_prueba,
				plan_programado_id: rawData.plan_programado_id,
				tarjeta_ultimos_4: rawData.tarjeta_ultimos_4,
				ciclo_facturacion: rawData.ciclo_facturacion,
				cancelar_al_fin_periodo: rawData.cancelar_al_fin_periodo,
				plan,
			};

			const result: SubscriptionResponse = {
				suscripcion,
			};

			const subscriptionResult = subscriptionResponseSchema.safeParse(result);
			if (!subscriptionResult.success) {
				console.error(
					"Invalid subscription response payload",
					subscriptionResult.error.flatten(),
				);
				return { suscripcion: null };
			}

			// Persist to memory + localStorage for fallback only
			_subCache = {
				userId: currentUserId,
				data: subscriptionResult.data,
			};
			try {
				localStorage.setItem("hip_sub_cache", JSON.stringify(_subCache));
			} catch {
				/* ignore */
			}

			return subscriptionResult.data;
		} catch (error) {
			if (_subCache && _subCache.userId === currentUserId) {
				console.warn("Using in-memory cached subscription data after request failure", error);
				return _subCache.data;
			}
			try {
				const stored = localStorage.getItem("hip_sub_cache");
				if (stored) {
					const parsed = JSON.parse(stored) as SubscriptionCacheEntry;
					if (parsed?.userId === currentUserId && parsed.data) {
						console.warn("Using localStorage cached subscription data after request failure", error);
						return parsed.data;
					}
				}
			} catch {
				// ignore
			}
			throw error;
		}
	},

	getPortalUrl: async (proveedor: string, mode: "manage" | "update" = "manage"): Promise<{ estado: string; mensaje: string; url?: string | null }> => {
		const provider = proveedor.toLowerCase();
		const endpoint = provider === "stripe" ? "/suscripcion/stripe/portal" : "/suscripcion/paddle/portal";
		const response = await axiosInstance.get(endpoint, { params: provider === "stripe" ? { mode } : undefined });
		return response.data;
	},
};

// Re-export for convenience
export * from "../schemas";
